import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Footer from '../components/layout/Footer';
import { featuredSection, galleryProjects } from '../data/mock';
import { X, ChevronLeft, ChevronRight, ZoomIn, MapPin } from 'lucide-react';
import { ScrollReveal, StaggerContainer, StaggerItem } from '../components/ui/ScrollReveal';

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxMode, setLightboxMode] = useState(null); // 'featured' | 'gallery'
  const [galleryImagesForLightbox, setGalleryImagesForLightbox] = useState([]);

  const featuredImages = featuredSection.images;

  const openFeaturedLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
    setLightboxMode('featured');
  };

  const openGalleryLightbox = (imageItem, project) => {
    const images = project.images.map((img) => ({
      id: img.id,
      title: project.title,
      category: project.work || 'Projects',
      image: img.url,
    }));
    const idx = project.images.findIndex((img) => img.id === imageItem.id);
    setSelectedImage(images[idx]);
    setCurrentIndex(idx);
    setGalleryImagesForLightbox(images);
    setLightboxMode('gallery');
  };

  const closeLightbox = useCallback(() => {
    setSelectedImage(null);
    setLightboxMode(null);
  }, []);

  const goToPrevious = useCallback(() => {
    if (lightboxMode === 'featured') {
      const newIndex = currentIndex === 0 ? featuredImages.length - 1 : currentIndex - 1;
      setCurrentIndex(newIndex);
      setSelectedImage(featuredImages[newIndex]);
    } else if (lightboxMode === 'gallery' && galleryImagesForLightbox.length > 0) {
      const newIndex = currentIndex === 0 ? galleryImagesForLightbox.length - 1 : currentIndex - 1;
      setCurrentIndex(newIndex);
      setSelectedImage(galleryImagesForLightbox[newIndex]);
    }
  }, [lightboxMode, currentIndex, featuredImages, galleryImagesForLightbox]);

  const goToNext = useCallback(() => {
    if (lightboxMode === 'featured') {
      const newIndex = currentIndex === featuredImages.length - 1 ? 0 : currentIndex + 1;
      setCurrentIndex(newIndex);
      setSelectedImage(featuredImages[newIndex]);
    } else if (lightboxMode === 'gallery' && galleryImagesForLightbox.length > 0) {
      const newIndex = currentIndex === galleryImagesForLightbox.length - 1 ? 0 : currentIndex + 1;
      setCurrentIndex(newIndex);
      setSelectedImage(galleryImagesForLightbox[newIndex]);
    }
  }, [lightboxMode, currentIndex, featuredImages, galleryImagesForLightbox]);

  // Lock body scroll and hide scroll indicator when lightbox is open
  useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = 'hidden';
      document.documentElement.classList.add('lightbox-open');
    } else {
      document.body.style.overflow = '';
      document.documentElement.classList.remove('lightbox-open');
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.classList.remove('lightbox-open');
    };
  }, [selectedImage]);

  // Keyboard navigation (featured only: ArrowLeft, ArrowRight; always: Escape)
  useEffect(() => {
    if (!selectedImage) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (lightboxMode === 'featured' || lightboxMode === 'gallery') {
        if (e.key === 'ArrowLeft') goToPrevious();
        if (e.key === 'ArrowRight') goToNext();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedImage, currentIndex, lightboxMode, goToNext, goToPrevious, closeLightbox]);

  return (
    <div className="min-h-screen bg-[#F5F2ED]">
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="pb-24 pt-24"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Featured Section - Full viewport hero with entrance animations */}
          {featuredImages.length > 0 && (
            <section id="featured" className="min-h-[calc(100vh-5rem)] flex flex-col pt-4 pb-16 md:pb-24">
              {/* Mobile-only page heading - matches other pages */}
              <div className="md:hidden mb-10">
                <span className="text-xs tracking-[0.2em] text-[#6B6B65] uppercase">
                  Our Visual Journey
                </span>
                <h1 className="font-serif text-4xl text-[#1A1A1A] mt-2 font-semibold">
                  Gallery
                </h1>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="mb-6 md:mb-8 flex flex-col md:flex-row md:items-start md:justify-between md:gap-8"
              >
                <div>
                  <span className="text-xs tracking-[0.25em] text-[#8B7355] uppercase font-medium">
                    Featured
                  </span>
                  <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] font-semibold mt-2">
                    {featuredSection.title}
                  </h2>
                </div>
                <div className="hidden md:block md:text-right">
                  <span className="text-xs tracking-[0.25em] text-[#8B7355] uppercase font-medium">
                    Our visual journey
                  </span>
                  <h2 className="font-serif text-2xl md:text-4xl lg:text-5xl text-[#1A1A1A] font-semibold mt-2">
                    Gallery
                  </h2>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="mb-6 md:mb-8 pl-0 md:pl-6 border-l-0 md:border-l-2 border-[#E0DDD9] max-w-2xl"
              >
                <p className="text-[#6B6B65] text-sm leading-relaxed">
                  {featuredSection.message}
                </p>
              </motion.div>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
                {/* Left - Featured3 (highlighted) */}
                <motion.div
                  initial={{ opacity: 0, y: 20, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 1, delay: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="lg:col-span-7 group relative cursor-pointer overflow-hidden rounded-2xl aspect-[4/3] lg:aspect-[16/10] shadow-lg hover:shadow-xl transition-all duration-500"
                >
                  <div className="absolute inset-0" onClick={() => openFeaturedLightbox(featuredImages[2], 2)}>
                    <img
                      src={featuredImages[2].image}
                      alt={featuredImages[2].title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                    />
                    <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors duration-300" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-14 h-14 rounded-full bg-white/25 backdrop-blur-md flex items-center justify-center border border-white/40">
                        <ZoomIn className="w-6 h-6 text-white" strokeWidth={2} />
                      </div>
                    </div>
                  </div>
                </motion.div>
                {/* Right - Featured1, 2, 4, 5 */}
                <div className="lg:col-span-5 grid grid-cols-2 gap-4 md:gap-6">
                  {[0, 1, 3, 4].map((origIndex, i) => {
                    const image = featuredImages[origIndex];
                    return (
                      <motion.div
                      key={image.id}
                      initial={{ opacity: 0, y: 16, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
                      className="group relative cursor-pointer overflow-hidden rounded-2xl aspect-square shadow-lg hover:shadow-xl transition-all duration-500"
                      onClick={() => openFeaturedLightbox(image, origIndex)}
                    >
                      <img
                        src={image.image}
                        alt={image.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-700 ease-out"
                      />
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/5 transition-colors duration-300" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-12 h-12 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center border border-white/40">
                          <ZoomIn className="w-5 h-5 text-white" strokeWidth={2} />
                        </div>
                      </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {galleryProjects.length > 0 && (
            <>
              <ScrollReveal variant="fadeUp" threshold={0.2}>
              <div className="relative my-8 md:my-10">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#E0DDD9]" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-[#F5F2ED] px-8 py-1.5 text-[11px] tracking-[0.2em] text-[#8B7355] uppercase font-medium">
                    More from our work
                  </span>
                </div>
              </div>
              </ScrollReveal>

              {/* Browse our collection */}
              <section id="gallery-grid" className="space-y-0">
                <div className="mb-6 md:mb-7">
                  <h2 className="font-serif text-2xl md:text-3xl text-[#1A1A1A] font-semibold tracking-tight">
                    Browse our collection
                  </h2>
                  <p className="text-[#6B6B65] text-sm mt-1.5 max-w-xl leading-relaxed">
                    Projects, sites, and moments that define our journey.
                  </p>
                </div>

                {galleryProjects.map((project, projectIdx) => (
                  <ScrollReveal key={project.id} variant="fadeUp" threshold={0.08}>
                    <article className={`flex flex-col lg:flex-row lg:gap-8 xl:gap-10 lg:items-start py-6 md:py-8 ${projectIdx > 0 ? 'border-t border-[#E0DDD9]/70' : ''}`}>
                        {/* Left: descriptions */}
                        <aside className="lg:w-64 xl:w-72 flex-shrink-0 mb-4 lg:mb-0 lg:sticky lg:top-28">
                          <div className="pl-3.5 border-l-2 border-[#8B7355]/50">
                            <h3 className="font-serif text-lg md:text-xl text-[#1A1A1A] font-semibold leading-snug">
                              {project.title}
                            </h3>
                            {project.work && (
                              <p className="text-[#8B7355] text-sm font-medium mt-1.5 tracking-wide">
                                {project.work}
                              </p>
                            )}
                            {project.location && (
                              <p className="text-[#5C5B57] text-sm mt-1.5 flex items-start gap-2">
                                <MapPin className="w-4 h-4 flex-shrink-0 text-[#8B7355]/80 mt-0.5" />
                                <span>{project.location}</span>
                              </p>
                            )}
                            <span className="inline-block mt-2.5 text-[10px] font-medium tracking-[0.2em] text-[#8A8884] uppercase">
                              {project.images.length} photo{project.images.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </aside>

                        {/* Right: horizontal scroll gallery */}
                        <div className="flex-1 min-w-0">
                          <div className="overflow-x-auto overflow-y-hidden scroll-smooth pb-2 -mb-2 snap-x snap-mandatory touch-pan-x">
                            <div className="flex gap-3.5 md:gap-4 flex-nowrap">
                              {project.images.map((img, imgIdx) => (
                                <div
                                  key={img.id}
                                  className="group cursor-pointer flex-shrink-0 w-[260px] sm:w-[300px] md:w-[340px] snap-center snap-always"
                                  onClick={() => openGalleryLightbox(img, project)}
                                >
                                  <div className="relative overflow-hidden rounded-xl bg-[#EBE8E4] shadow-md ring-1 ring-[#E0DDD9]/50 hover:ring-[#8B7355]/40 hover:shadow-xl transition-all duration-500 group-hover:-translate-y-1 aspect-[4/3]">
                                    <img
                                      src={img.url}
                                      alt={`${project.title} - ${imgIdx + 1}`}
                                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-600 ease-out"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                      <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl group-hover:scale-105 transition-transform duration-300">
                                        <ZoomIn className="w-7 h-7 text-[#1A1A1A]" strokeWidth={2} />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                    </article>
                  </ScrollReveal>
                ))}
              </section>
            </>
          )}
        </div>

        {/* Lightbox */}
        <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[10000] bg-black/95 flex items-center justify-center p-4 md:p-8"
            onClick={closeLightbox}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all duration-200 z-10"
            >
              <X className="w-6 h-6" strokeWidth={2} />
            </button>
            {(lightboxMode === 'featured' || lightboxMode === 'gallery') && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                  className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/90 hover:bg-white text-[#1A1A1A] flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 z-10"
                >
                  <ChevronLeft className="w-7 h-7 md:w-8 md:h-8" strokeWidth={2.5} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); goToNext(); }}
                  className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/90 hover:bg-white text-[#1A1A1A] flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 z-10"
                >
                  <ChevronRight className="w-7 h-7 md:w-8 md:h-8" strokeWidth={2.5} />
                </button>
              </>
            )}
            <div className="w-full h-full max-w-7xl flex flex-col items-center justify-center" onClick={(e) => e.stopPropagation()}>
              <div className="text-center mb-6">
                <p className="text-[11px] text-[#C9A962] tracking-[0.2em] uppercase mb-2">{selectedImage.category}</p>
                <h3 className="text-white font-serif text-xl md:text-2xl font-medium">{selectedImage.title}</h3>
                {(lightboxMode === 'featured' || lightboxMode === 'gallery') && (
                  <p className="text-white/60 text-xs mt-2">
                    {currentIndex + 1} / {lightboxMode === 'featured' ? featuredImages.length : galleryImagesForLightbox.length}
                  </p>
                )}
              </div>
              <motion.img
                key={currentIndex}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
                src={selectedImage.image}
                alt={selectedImage.title}
                className="max-w-full max-h-[75vh] md:max-h-[80vh] w-auto h-auto object-contain"
              />
            </div>
          </motion.div>
        )}
        </AnimatePresence>
      </motion.main>
      <Footer />
    </div>
  );
};

export default GalleryPage;
